/**
 * A miniatura de uma foto do signatário (E80.F1.T06).
 *
 * A rota do blob exige autenticação no cabeçalho, então a imagem não pode ir
 * direto no `src`: o componente busca os bytes e monta uma URL local, no mesmo
 * padrão do `useSignatureBlob` do workspace.
 *
 * Mostra a versão **efetiva** — com o recorte e o giro aplicados —, que é o que
 * o perito escolheu enquadrar. O original continua a um pedido de distância, e
 * é o que o editor de recorte usa.
 */
import { useEffect, useState } from 'react';

import { ptBR } from '@/i18n/pt-BR';
import { useApiClient } from '@/lib/api';
import { baixarFotoDoSignatario, type FotoDoSignatarioData } from '@/lib/fotos_do_signatario';

const t = ptBR.exameEletronico.fotosDoSignatario;

export interface FotoDoSignatarioThumbProps {
  caseId: string;
  foto: FotoDoSignatarioData;
}

export function FotoDoSignatarioThumb({ caseId, foto }: FotoDoSignatarioThumbProps): JSX.Element {
  const client = useApiClient();
  const [url, setUrl] = useState<string | null>(null);

  // A chave do efeito inclui a geometria: recortar não muda o id da foto, e sem
  // isso a miniatura continuaria mostrando o enquadramento anterior.
  const geometria = `${foto.bounding_box === null ? 'inteira' : 'recorte'}:${foto.rotacao_deg}`;

  useEffect(() => {
    let cancelado = false;
    let criada: string | null = null;
    void (async () => {
      try {
        const blob = await baixarFotoDoSignatario(client, caseId, foto.id);
        if (cancelado) return;
        criada = URL.createObjectURL(blob);
        setUrl(criada);
      } catch {
        if (!cancelado) setUrl(null);
      }
    })();
    return () => {
      cancelado = true;
      if (criada !== null) URL.revokeObjectURL(criada);
    };
  }, [client, caseId, foto.id, geometria]);

  return (
    <div
      className="flex h-20 w-20 shrink-0 items-center justify-center overflow-hidden border border-border bg-surface-2"
      data-testid={`foto-thumb-${foto.id}`}
    >
      {url === null ? (
        <span className="text-caption text-fg-dim">{t.recorte.carregando}</span>
      ) : (
        <img src={url} alt={foto.nome_arquivo} className="h-full w-full object-cover" />
      )}
    </div>
  );
}
